/**
 * L2 blocks — SectionHeader · KeyValueList · TextBlock · Disclosure · Timeline.
 *
 * The small structural modules every detail surface repeats: a heading with a
 * count, a table of label/value rows, a body of long text, a collapsible
 * section, a milestone strip. None of them owns data
 * behaviour — each binds a list or a value and renders it — which is exactly
 * why they are generic L2 and not per-widget code.
 *
 * They exist because the alternative is worse: without them a blueprint either
 * hand-builds the same Stack-of-Text six times per widget (and drifts), or a
 * domain L3 gets invented for what is really a heading.
 */
import { useEffect, useState } from 'react';
import { TYPE_STYLE, toneColor, toneSoft, gapValue, resetButton, hitTarget, fmt, kvLines, waitsForData } from './ui';
import { Image } from './primitives';
import { ownCard } from './ownCard';
import { num } from '../paths';
import { SkeletonText } from './feedback';

/** Every block reads its list off `props.<name>` or off the node's own bind. */
function listOf(source, data) {
  const raw = source !== undefined ? source : data;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

/**
 * SectionHeader — a heading, an optional COUNT pill, an optional trailing
 * action. Repeated on essentially every detail surface, which is the whole
 * argument for it being one component instead of six hand-built rows.
 *
 * The count is bound and formatted through the kit's ONE formatter, so a
 * heading and the list under it can never disagree about how many there are.
 *
 * FOUR MORE READINGS, because a heading over a shelf is doing more work than a
 * heading over a list, and every one of them was being dropped:
 *   `sub`   a second line under the title — what the section IS ("Current
 *           cultural moments"), which the title alone never says.
 *   `meta`  right-aligned free text ("11 templates"). It is NOT the `count`
 *           pill: a pill is a chip of chrome, and prod's is a phrase. Both may
 *           be present; they are different readings of the same section.
 *   `hint`  the discoverability affordance for a section the user has to
 *           SCROLL ("Swipe →"). On a rail with no scrollbar, dropping it costs
 *           the user every item past the second one.
 *   `tone`  the title's status colour, for the one heading that is itself a
 *           warning. Absent, the title keeps the plain ink it always had.
 * With none of them declared this renders byte-for-byte what it always did:
 * the four-part head is a DIFFERENT shape (a copy column beside a meta
 * column), and a heading that asked for none of it must not silently move.
 */
export function SectionHeader({ variant, node = null, props = {}, data, ctx, emit }) {
  const title = props.title !== undefined ? props.title : data;
  const count = props.count;
  const action = props.action || null;
  const sub = props.sub == null || props.sub === '' ? null : String(props.sub);
  // `meta` is the one line here that is routinely a COUNT phrased as copy
  // ("11 templates"), so it is the one that reads "0 templates" for a moment
  // while the feed lands. Shimmer it until the read it interpolates is real.
  const metaWaits = waitsForData(ctx, node?.props?.meta);
  const meta = props.meta == null || props.meta === '' ? null : String(props.meta);
  const hint = props.hint == null || props.hint === '' ? null : String(props.hint);
  // OPT-IN inline action (default false = the stacked four-part head, unchanged):
  // with `inlineAction` AND a `meta` present, `meta` and the trailing action sit
  // on ONE horizontal row instead of stacking. A heading that asked for none of
  // it must not silently move, so this is gated on the prop AND on there being a
  // meta+action pair to inline.
  const inlineAction = props.inlineAction === true;
  const titleStyle = {
    ...(variant === 'eyebrow' ? TYPE_STYLE.eyebrow : TYPE_STYLE.subtitle),
    color: props.tone ? toneColor(props.tone) : (variant === 'eyebrow' ? 'var(--text-3)' : 'var(--text)'),
    fontFamily: variant === 'eyebrow' ? undefined : 'var(--font-display)',
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const countPill = count != null && count !== '' ? (
    <span style={{
      flex: '0 0 auto', padding: '1px 8px', borderRadius: 'var(--r-pill)',
      background: 'var(--surface-3)', color: 'var(--text-3)',
      fontSize: 'var(--t-xs)', fontWeight: 700,
    }}>{fmt(count)}</span>
  ) : null;
  const actionButton = action ? (
    <button
      type="button"
      onClick={() => emit?.('actionActivated', { action: action.action || action.key || action.label })}
      style={{ ...resetButton, color: action.tone ? toneColor(action.tone) : 'var(--accent)', fontSize: 'var(--t-sm)', fontWeight: 600, flex: '0 0 auto' }}
    >{action.label || 'More'}</button>
  ) : null;

  if (sub || meta || hint || metaWaits) {
    const metaNode = !metaWaits && meta ? (
      <span style={{
        fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text-3)', whiteSpace: 'nowrap',
      }}>{meta}</span>
    ) : null;
    const subNode = sub ? (
      <div style={{
        marginTop: 'var(--sp-1)', fontSize: 'var(--t-sm)',
        color: 'var(--text-3)', lineHeight: 1.3,
      }}>{sub}</div>
    ) : null;
    const hintNode = hint ? (
      <span style={{
        fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text-2)', whiteSpace: 'nowrap',
      }}>{hint}</span>
    ) : null;
    // `meta` + `hint` share ONE right-aligned row when BOTH are present — the
    // default (non-`inlineAction`) head: scene-studio rails put "12 templates"
    // and "Swipe →" on one line, not stacked. Only one of the two present → the
    // stacked slot is unchanged. The Flutter twin composes the same single row
    // (`buildViewIrSectionHeader`).
    const metaHintRow = metaNode && hintNode;

    // INLINE ACTION (opt-in): the meta rides on the LEFT beside the title and the
    // action sits ALONE on the far right, so the row reads
    // `[title  meta] ————— [action]` — `justify-content: space-between` opens the
    // gap. This is the shape a 'N templates … More' head wants. A default header
    // (no `inlineAction`) keeps the stacked four-part head below, byte-identical.
    if (inlineAction && metaNode && actionButton) {
      return (
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 'var(--sp-3)', minWidth: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
              <div style={titleStyle}>{title == null ? '' : String(title)}</div>
              {countPill}
              {metaNode}
            </div>
            {subNode}
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {hintNode}
            {actionButton}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 'var(--sp-3)', minWidth: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle}>{title == null ? '' : String(title)}</div>
          {subNode}
        </div>
        <div style={{
          flex: '0 0 auto', display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 2,
        }}>
          {countPill}
          {metaWaits ? <SkeletonText width={64} height={10} /> : null}
          {metaHintRow ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              {metaNode}
              {hintNode}
            </div>
          ) : (
            <>
              {metaNode}
              {hintNode}
            </>
          )}
          {actionButton}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
      <div style={titleStyle}>{title == null ? '' : String(title)}</div>
      {countPill}
      <span style={{ flex: 1 }} />
      {actionButton}
    </div>
  );
}

/**
 * The VALUE half of one kv row: a scalar reads as one line, an object reads as
 * its own small table (`kvLines`), and an enum reads through `labels`.
 */
function KeyValueCell({ lines, tone, style }) {
  if (lines.length === 1 && !lines[0].label) {
    return <span style={style}>{lines[0].text}</span>;
  }
  return (
    <span style={{ ...style, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {lines.map((l, i) => (
        <span key={`${l.label || 'l'}-${i}`} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
          {l.label ? (
            <span style={{ color: 'var(--text-3)', flex: '0 0 auto', fontWeight: 500 }}>{l.label}</span>
          ) : null}
          <span style={{ minWidth: 0, color: toneColor(tone) }}>{l.text}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * KeyValueList — the detail rows every data view ends in.
 *
 * SEALED: an EMPTY value is DROPPED, not drawn blank (`hideEmpty`, default
 * on). A row that reads "Brand —" says the field exists and is unknown; on a
 * detail page that is noise, and the honest render of "we do not know" is
 * silence. Two shapes from one component: stacked rows (`columns: 1`) and a
 * tile grid (`columns: 2`), because a facts grid and a details table differ by
 * column count, not by contract.
 *
 * TWO READINGS of the value it prints, both from `kvLines`:
 *   `labels: {field: {value: "Label"}}` decodes an ENUM — the row stores
 *   `supplement` and the page says "Supplement", without a derive per enum.
 *   An OBJECT field draws as its own small table instead of `JSON.stringify`,
 *   which is what put `{"verdict":"caution",…}` on a nutrition page.
 */
export function KeyValueList({ variant, props = {}, data }) {
  const items = listOf(props.items, data)
    .map((it) => {
      const field = it.key ?? it.field ?? it.label ?? '';
      return {
        label: it.label ?? it.key ?? '',
        lines: kvLines(it.value, { labels: props.labels, field, exclude: props.exclude }),
        tone: it.tone,
      };
    })
    .filter((it) => (props.hideEmpty === false) || it.lines.length);
  if (!items.length) return null;

  const columns = Number(props.columns) || (variant === 'grid' ? 2 : 1);

  if (columns > 1) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`, gap: 'var(--sp-2)' }}>
        {items.map((it, i) => (
          <div key={`${it.label}-${i}`} style={{
            background: 'var(--surface)', borderRadius: 'var(--r)', padding: 'var(--sp-3)',
            boxShadow: 'var(--shadow-1)', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0,
          }}>
            <span style={{ ...TYPE_STYLE.eyebrow, color: 'var(--text-3)' }}>{it.label}</span>
            <KeyValueCell
              lines={it.lines}
              tone={it.tone}
              style={{
                fontSize: 'var(--t-md)', fontWeight: 600, color: toneColor(it.tone), minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <dl style={{
      margin: 0, background: 'var(--surface)', borderRadius: 'var(--r)',
      padding: '2px var(--sp-4)', boxShadow: 'var(--shadow-1)',
    }}>
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`} style={{
          display: 'flex', gap: 'var(--sp-3)', padding: '10px 0',
          borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
        }}>
          <dt style={{ flex: '0 0 40%', fontSize: 'var(--t-sm)', color: 'var(--text-3)' }}>{it.label}</dt>
          <dd style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 'var(--t-sm)', wordBreak: 'break-word' }}>
            <KeyValueCell lines={it.lines} tone={it.tone} style={{ color: toneColor(it.tone) }} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One block of a `prose` body — the closed set a declared document may use. */
function ProseBlock({ block }) {
  const type = block?.block || block?.type || 'paragraph';
  switch (type) {
    case 'heading':
      return <div style={{ ...TYPE_STYLE.subtitle, fontFamily: 'var(--font-display)' }}>{block.text}</div>;
    case 'bullets':
      return (
        <ul style={{ margin: 0, paddingLeft: '1.1em', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(block.items || []).map((t, i) => (
            <li key={i} style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.5 }}>
              {typeof t === 'string' ? t : (t?.text ?? '')}
            </li>
          ))}
        </ul>
      );
    case 'figure':
      return (
        <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Image props={{ src: block.src, fit: 'cover', ratio: block.ratio || '4/3' }} />
          {block.caption ? (
            <figcaption style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)' }}>{block.caption}</figcaption>
          ) : null}
        </figure>
      );
    case 'chips':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(block.items || []).map((c, i) => {
            const label = typeof c === 'string' ? c : (c?.text ?? c?.label ?? '');
            const tone = typeof c === 'object' ? c?.tone : undefined;
            return (
              <span key={`${label}-${i}`} style={{
                padding: '2px 8px', borderRadius: 'var(--r-pill)',
                background: toneSoft(tone), color: toneColor(tone),
                fontSize: 'var(--t-xs)', fontWeight: 700,
              }}>{label}</span>
            );
          })}
        </div>
      );
    default:
      return (
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {typeof block === 'string' ? block : (block?.text ?? '')}
        </p>
      );
  }
}

/**
 * TextBlock — LONG text, which `Text` (a typographic run) is deliberately not.
 *
 * Three readings from one contract: `plain` (a multi-paragraph body, newlines
 * preserved), `mono` (extracted / machine text, wrapped and its own scroll so
 * a 4 000-character OCR dump cannot push the rest of the page off screen), and
 * `prose` (a DECLARED block sequence — heading · paragraph · bullets · figure ·
 * chips — which is how a model-written document renders without a per-widget
 * card).
 *
 * SEALED: `hideWhenEmpty` (default on) — an empty body draws NOTHING, never a
 * section heading over an empty well.
 */
export function TextBlock({ variant, props = {}, data }) {
  const value = props.value !== undefined ? props.value : data;
  const kind = props.variant || variant || 'plain';
  const empty = value == null || value === ''
    || (Array.isArray(value) && !value.length);
  if (empty && props.hideWhenEmpty !== false) return null;

  const box = {
    maxHeight: props.maxHeight || undefined,
    overflowY: props.scroll ? 'auto' : undefined,
    scrollbarWidth: props.scroll ? 'none' : undefined,
  };

  if (kind === 'prose') {
    const blocks = Array.isArray(value) ? value : [value];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', minWidth: 0, ...box }}>
        {blocks.map((b, i) => <ProseBlock key={i} block={b} />)}
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: kind === 'mono' ? 'var(--font-mono)' : 'inherit',
      fontSize: kind === 'mono' ? 'var(--t-xs)' : 'var(--t-sm)',
      color: 'var(--text-2)', lineHeight: 1.55,
      whiteSpace: props.wrap === 'normal' ? 'normal' : 'pre-wrap',
      wordBreak: 'break-word', minWidth: 0,
      background: kind === 'mono' ? 'var(--surface-2)' : undefined,
      borderRadius: kind === 'mono' ? 'var(--r-sm)' : undefined,
      padding: kind === 'mono' ? 'var(--sp-3)' : undefined,
      ...box,
    }}>
      {Array.isArray(value) ? value.filter(Boolean).join('\n') : String(value ?? '')}
    </div>
  );
}

/**
 * Disclosure — a summary row that reveals its children.
 *
 * Its whole job is to keep a secondary affordance ON the page without spending
 * the page's attention on it, which is why its fallback is `Stack`: an app
 * that cannot collapse simply shows the content, and nothing is lost but the
 * folding.
 */
export function Disclosure({ props = {}, emit, slots, children }) {
  const [shown, setOpen] = useState(Boolean(props.open));
  const setOpenSlot = slots?.setOpen;

  // The slot SEEDS the fold; the user can still fold it back, which a
  // shadowing read of the inbox would have made impossible.
  useEffect(() => {
    if (setOpenSlot?.payload?.open !== undefined) setOpen(Boolean(setOpenSlot.payload.open));
  }, [setOpenSlot]);

  const toggle = () => {
    const next = !shown;
    setOpen(next);
    emit?.('toggled', { open: next });
  };

  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-1)',
      padding: 'var(--sp-2) var(--sp-3)', minWidth: 0,
    }}>
      <button
        type="button"
        aria-expanded={shown}
        onClick={toggle}
        style={{
          ...resetButton, ...hitTarget,
          width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text)',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>{props.summary}</span>
        <span aria-hidden style={{
          color: 'var(--text-3)', transition: 'transform 160ms var(--ease)',
          transform: shown ? 'rotate(90deg)' : 'none',
        }}>›</span>
      </button>
      {shown ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: gapValue(props.gap, 'var(--sp-3)'), padding: 'var(--sp-2) 0' }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Timeline — a CONNECTED milestone strip: node · primary · secondary, in
 * order, with the connector drawn between them.
 *
 * Horizontal by default (a due-date strip), and its declared fallback is a
 * Stack, which is the same rows read vertically — the connector is the form,
 * the sequence is the function.
 */
export function Timeline({ variant, props = {}, data, emit }) {
  const nodes = listOf(props.nodes, data);
  if (!nodes.length) return null;
  const column = variant === 'column' || props.direction === 'column';

  return (
    <div style={{
      display: 'flex', flexDirection: column ? 'column' : 'row',
      gap: 0, overflowX: column ? undefined : 'auto', scrollbarWidth: 'none', minWidth: 0,
    }}>
      {nodes.map((n, i) => {
        const tone = n.tone || 'accent';
        return (
          <div
            key={`${n.node ?? n.primary ?? i}-${i}`}
            onClick={emit ? () => emit('nodeActivated', { index: i, node: n.node ?? null }) : undefined}
            style={{
              display: 'flex', flexDirection: column ? 'row' : 'column',
              alignItems: column ? 'flex-start' : 'center',
              gap: 'var(--sp-2)', flex: column ? undefined : '1 0 92px', minWidth: 0,
              position: 'relative', padding: column ? '0 0 var(--sp-3)' : '0 var(--sp-2)',
              cursor: emit ? 'pointer' : 'default',
            }}
          >
            {/* the connector: a hairline to the NEXT node, never past the last */}
            {i < nodes.length - 1 ? (
              <span aria-hidden style={column
                ? { position: 'absolute', left: 9, top: 20, bottom: 0, width: 1, background: 'var(--line)' }
                : { position: 'absolute', top: 9, left: '50%', right: '-50%', height: 1, background: 'var(--line)' }} />
            ) : null}
            <span style={{
              width: 20, height: 20, borderRadius: 10, flex: '0 0 auto', zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: toneSoft(tone), color: toneColor(tone),
              border: `1px solid ${toneColor(tone)}`,
              fontSize: 'var(--t-xs)', fontWeight: 800,
            }}>{n.node ?? ''}</span>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0,
              textAlign: column ? 'left' : 'center',
            }}>
              <span style={{
                fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text)',
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{n.primary ?? ''}</span>
              {n.secondary ? (
                <span style={{
                  fontSize: 'var(--t-xs)', color: 'var(--text-3)',
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{n.secondary}</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ProposalCard (L1) — ONE proposal put to the user, with what it takes to judge
 * it and the two ways to answer.
 *
 * Thumbnail · title + subtitle · qualifying chips · a note panel carrying the
 * REASONING · an actions row whose FIRST action is the affirmative one.
 *
 * The note is what separates this from a plain Card: a proposal whose reasoning
 * the user cannot see is a guess, and a card with no second action is an
 * announcement rather than a proposal.
 */
function ProposalCardBare({ props = {}, data, emit, slots }) {
  const pushed = slots?.setProposal?.payload || {};
  const title = pushed.title ?? props.title ?? data?.title ?? '';
  const subtitle = pushed.subtitle ?? props.subtitle ?? data?.subtitle ?? null;
  const media = props.media ?? data?.media ?? null;
  const note = pushed.note ?? props.note ?? data?.note ?? null;
  // A note carries `text` (one passage) or `lines[]` (broken where the author
  // chose) — the same shape NotePanel takes, so a note reads identically whether
  // it rides a proposal or stands on its own.
  const noteText = note && typeof note === 'object' ? note.text : null;
  const noteLines = Array.isArray(note?.lines) ? note.lines.filter(Boolean) : [];
  const rawChips = pushed.chips ?? props.chips ?? (data ? data.chips : null);
  const chips = (Array.isArray(rawChips) ? rawChips : [])
    .map((c) => (c && typeof c === 'object' ? c : { label: c }))
    .filter((c) => c.label);
  const rawActions = props.actions ?? (data ? data.actions : null);
  const actions = (Array.isArray(rawActions) ? rawActions : []).filter((a) => a && a.label);
  const header = props.header ?? (data ? data.header : null);
  const meta = props.meta ?? (data ? data.meta : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', minWidth: 0 }}>
      {header || meta ? (
        // The card's own header: what this card IS on the left, the one reading
        // that qualifies it on the right. Same surface as the body — a header in
        // its own panel is a second card.
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'var(--sp-3)', minWidth: 0,
        }}>
          {header ? (
            <span style={{
              fontSize: 'var(--t-xl)', fontWeight: 800, letterSpacing: '-0.025em',
              color: 'var(--text)', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{header}</span>
          ) : <span />}
          {meta ? (
            <span data-testid="proposal-meta" style={{
              fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text)', flex: '0 0 auto',
            }}>{meta}</span>
          ) : null}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-start', minWidth: 0 }}>
        {media ? (
          <div style={{
            width: 102, flex: '0 0 auto',
            borderRadius: 16, overflow: 'hidden', border: '1px solid var(--surface-3)',
          }}>
            {/* the RATIO rides the Image, not this box: the frame then holds its
                shape while the picture loads, fails, or is a `media://` handle the
                host has not signed yet — otherwise the thumb jumps as it resolves. */}
            <Image props={{ src: media, fit: 'cover', ratio: props.aspect || '102 / 122' }} />
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0, flex: 1 }}>
          <span style={{
            fontSize: 'var(--t-xl)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--text)',
          }}>{title}</span>
          {subtitle ? (
            <span style={{ fontSize: 'var(--t-sm)', fontWeight: 500, lineHeight: 1.4, color: 'var(--text-3)' }}>
              {subtitle}
            </span>
          ) : null}
          {chips.length ? (
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 'var(--sp-1)' }}>
              {chips.map((c, i) => (
                <span
                  key={`${c.label}-${i}`}
                  data-testid="proposal-chip"
                  style={{
                    fontSize: 'var(--t-xs)', fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                    background: 'var(--surface-3)',
                    color: c.tone === 'muted' ? 'var(--text-3)' : 'var(--text)',
                  }}
                >{c.label}</span>
              ))}
            </span>
          ) : null}
        </div>
      </div>

      {note && (note.lead || noteText || noteLines.length) ? (
        <div
          data-testid="proposal-note"
          style={{
            background: 'var(--surface-3)', borderRadius: 12, padding: 'var(--sp-4)',
            fontSize: 'var(--t-sm)', lineHeight: 1.6, color: 'var(--text)', minWidth: 0,
          }}
        >
          {note.lead ? <strong style={{ fontWeight: 800 }}>{note.lead}</strong> : null}
          {noteText ? <span>{note.lead ? ' ' : ''}{noteText}</span> : null}
          {noteLines.map((line, i) => (
            <div key={`${line}-${i}`}>{line}</div>
          ))}
        </div>
      ) : null}

      {actions.length ? (
        <div style={{ display: 'flex', gap: 'var(--sp-3)', minWidth: 0 }}>
          {actions.map((a, i) => {
            // SEALED: the FIRST action is the affirmative one, so it is the
            // filled button and the rest are outlined — the shape of the answer
            // is the kit's, the words are the blueprint's.
            const primary = i === 0 ? a.variant !== 'secondary' : a.variant === 'primary';
            return (
              <button
                key={a.key ?? `${a.label}-${i}`}
                type="button"
                data-testid="proposal-action"
                data-primary={primary ? '1' : '0'}
                onClick={emit ? () => emit('actionActivated', { key: a.key ?? null, index: i }) : undefined}
                style={{
                  ...resetButton, flex: 1, minWidth: 0, height: 48, borderRadius: 14,
                  // `resetButton` left-aligns (it is written for text rows); an
                  // action button centres its label, and centring the BOX is what
                  // keeps a two-line label centred too.
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  textAlign: 'center',
                  fontSize: 'var(--t-sm)', fontWeight: 800,
                  background: primary ? 'var(--text)' : 'transparent',
                  color: primary ? 'var(--surface)' : 'var(--text)',
                  border: primary ? 'none' : '1px solid var(--line)',
                  cursor: emit ? 'pointer' : 'default',
                }}
              >{a.label}</button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * NotePanel (L1) — a quiet panel holding one short passage the reader is meant
 * to actually read.
 *
 * The bold `lead` ("Suggestion:", "AI insight:", "in your words:") is what makes
 * it a note rather than a second subtitle: a paragraph with no name reads as
 * decoration and gets skipped. `variant="quote"` sets the passage in the
 * monospace face, for words quoted back to the user verbatim.
 */
function NotePanelBare({ variant, props = {}, data, slots }) {
  const pushed = slots?.setNote?.payload || {};
  const lead = pushed.lead ?? props.lead ?? (data ? data.lead : null);
  const text = pushed.text ?? props.text ?? (data ? data.text : null);
  const rawLines = pushed.lines ?? props.lines ?? (data ? data.lines : null);
  const lines = (Array.isArray(rawLines) ? rawLines : []).filter(Boolean);

  // An empty panel draws nothing — a grey box with no passage is furniture.
  if (!lead && !text && !lines.length) return null;

  const quote = variant === 'quote';
  return (
    <div
      data-testid="note-panel"
      style={{
        background: 'var(--surface-3)', borderRadius: 12, padding: 'var(--sp-4)',
        fontSize: 'var(--t-xs)', lineHeight: 1.6, color: toneColor(props.tone),
        fontFamily: quote ? 'var(--font-mono, ui-monospace, monospace)' : undefined,
        minWidth: 0,
      }}
    >
      {lead ? <strong style={{ fontWeight: 800 }}>{lead}</strong> : null}
      {text ? <span>{lead ? ' ' : ''}{text}</span> : null}
      {lines.map((line, i) => <div key={`${line}-${i}`}>{line}</div>)}
    </div>
  );
}

/**
 * DeliveryCard (L1) — ONE delivery from a run the user did not watch, laid out
 * so it can be judged in a glance and opened in a tap.
 *
 * Header/meta · a compact row (square thumb, title, how it came to be, the
 * facts) closed by a hairline · the SUMMARY panel (what the run did) · the
 * QUOTE panel (what the user asked, in the monospace face so it reads as
 * quoted) · ONE full-width action.
 *
 * Deliberately not a ProposalCard: that one puts a choice to the user and takes
 * two answers; this one reports a finished job and takes one — and its spacing
 * is its own. The pixel values below are the design's, not the proposal's.
 */
function DeliveryCardBare({ props = {}, data, emit, slots }) {
  const pushed = slots?.setDelivery?.payload || {};
  const header = props.header ?? (data ? data.header : null);
  const meta = props.meta ?? (data ? data.meta : null);
  const media = props.media ?? (data ? data.media : null);
  const title = pushed.title ?? props.title ?? (data ? data.title : '') ?? '';
  const subtitle = pushed.subtitle ?? props.subtitle ?? (data ? data.subtitle : null);
  const rawFacts = pushed.facts ?? props.facts ?? (data ? data.facts : null);
  const facts = (Array.isArray(rawFacts) ? rawFacts : []).filter((f) => f && (f.label || f.value));
  const summary = pushed.summary ?? props.summary ?? (data ? data.summary : null);
  const quote = props.quote ?? (data ? data.quote : null);
  const quoteText = quote && typeof quote === 'object' ? quote.text : null;
  const action = props.action ?? (data ? data.action : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {header || meta ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, minWidth: 0, marginBottom: 20,
        }}>
          <span style={{
            fontSize: 20, fontWeight: 800, letterSpacing: -0.5, color: 'var(--text)',
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{header ?? ''}</span>
          {meta ? (
            <span data-testid="delivery-meta"
              style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', flex: '0 0 auto' }}>{meta}</span>
          ) : null}
        </div>
      ) : null}

      {/* the row: thumb · title · how it came to be · facts — closed by a hairline */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, minWidth: 0,
        paddingBottom: 20, borderBottom: '1px solid var(--surface-3)',
      }}>
        {media ? (
          <div style={{
            width: 73, height: 73, flex: '0 0 auto', borderRadius: 16, overflow: 'hidden',
            border: '1px solid var(--surface-3)',
          }}>
            <Image props={{ src: media, fit: 'cover', ratio: '1/1' }} />
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={{
            fontSize: 16, fontWeight: 800, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{title}</span>
          {subtitle ? (
            <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.5, color: 'var(--text-3)', marginTop: 6 }}>
              {subtitle}
            </span>
          ) : null}
          {facts.length ? (
            <span style={{ display: 'flex', gap: 16, marginTop: 8, minWidth: 0, flexWrap: 'wrap' }}>
              {facts.map((f, i) => (
                <span key={`${f.label}-${i}`} data-testid="delivery-fact"
                  style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)' }}>{f.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{f.value}</span>
                </span>
              ))}
            </span>
          ) : null}
        </div>
      </div>

      {summary ? (
        <div data-testid="delivery-summary" style={{
          marginTop: 24, background: 'var(--surface-3)', borderRadius: 12, padding: 12,
          fontSize: 13, lineHeight: 1.5, color: 'var(--text)', minWidth: 0,
        }}>{summary}</div>
      ) : null}

      {quoteText ? (
        <div data-testid="delivery-quote" style={{
          marginTop: 28, background: 'var(--surface-3)', borderRadius: 12, padding: 12,
          fontSize: 12, lineHeight: 1.5, color: 'var(--text)', minWidth: 0,
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        }}>
          {quote.lead ? (
            <div style={{ fontWeight: 700, color: 'var(--text-3)' }}>{quote.lead}</div>
          ) : null}
          <div>{quoteText}</div>
        </div>
      ) : null}

      {action && action.label ? (
        <button
          type="button"
          data-testid="delivery-action"
          onClick={emit ? () => emit('actionActivated', { key: action.key ?? null }) : undefined}
          style={{
            ...resetButton, marginTop: 40, height: 45, borderRadius: 14, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            fontSize: 14, fontWeight: 800, background: 'var(--text)', color: 'var(--surface)',
            cursor: emit ? 'pointer' : 'default',
          }}
        >{action.label}</button>
      ) : null}
    </div>
  );
}

/**
 * DeckCard (L1) — the cover of a deck: a named set of things, with a peek at
 * what is inside.
 *
 * An uppercase eyebrow · the title · one line of copy (how much is in it) · a
 * FAN of the top covers, shingled so a handful reads as a stack rather than a
 * row. It carries its OWN surface (a gradient the blueprint may choose) — that
 * is the point of a cover — so on a dashboard the node is `bare: true`, or the
 * shell boxes a card in a card. The whole card is the tap target and emits
 * `activated`. Spacing is the design's own (6 · 4 · 14; 20 padding; 20 radius;
 * 61×81 covers stepped 45).
 */
const DECK_COVER = { w: 61, h: 81, step: 45 };

/** `#RRGGBB` → relative luminance (0..1); anything else → null (a token, not a colour). */
function hexLuminance(raw) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(raw ?? '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const ch = (v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * ch((n >> 16) & 255) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
}

/** A blueprint-authored plate reads as LIGHT when the midpoint of its two stops is. */
function plateIsLight(from, to) {
  const a = hexLuminance(from); const b = hexLuminance(to);
  if (a == null || b == null) return false;
  return (a + b) / 2 > 0.5;
}

export function DeckCard({ props = {}, data, emit, slots }) {
  const pushed = slots?.setDeck?.payload || {};
  const pick = (name) => (pushed[name] !== undefined ? pushed[name] : (props[name] !== undefined ? props[name] : data?.[name]));
  const eyebrow = props.eyebrow ?? (data ? data.eyebrow : null);
  const title = pick('title');
  const sub = pick('sub');
  const cap = Math.max(1, Math.round(num(props.maxCovers ?? 4) || 4));
  const coverSource = pick('covers');
  const covers = (Array.isArray(coverSource) ? coverSource : [])
    .map((c) => (typeof c === 'string' ? { src: c } : c))
    .filter((c) => c && typeof c === 'object')
    .slice(0, cap);
  const ownPlate = Array.isArray(props.gradient) && props.gradient.length >= 2;
  const [from, to] = ownPlate ? props.gradient : ['var(--surface-2)', 'var(--surface-3)'];
  // The ink follows the PLATE when the plate is the blueprint's, not the theme:
  // a cream cover in dark mode with the theme's white ink loses its title.
  // Without a gradient the plate is a token surface and the theme ink is right.
  const onLight = ownPlate && plateIsLight(from, to);
  const ink = !ownPlate ? 'var(--text)' : (onLight ? '#1C1C1E' : '#FFFFFF');
  const inkMuted = !ownPlate ? 'var(--text-3)' : '#8E8E93';
  const fanWidth = covers.length ? DECK_COVER.w + DECK_COVER.step * (covers.length - 1) : 0;
  const ellipsis = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <button
      type="button"
      data-testid="deck-card"
      onClick={emit ? () => emit('activated', {}) : undefined}
      style={{
        ...resetButton, display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        width: '100%', minWidth: 0, padding: 20, borderRadius: 20, boxSizing: 'border-box',
        backgroundImage: `linear-gradient(135deg, ${from}, ${to})`,
        cursor: emit ? 'pointer' : 'default',
      }}
    >
      {eyebrow ? (
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
          color: inkMuted, marginBottom: 6, ...ellipsis,
        }}>{eyebrow}</span>
      ) : null}
      {title ? (
        <span data-testid="deck-title" style={{
          fontSize: 22, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.15, color: ink, ...ellipsis,
        }}>{title}</span>
      ) : null}
      {sub ? (
        <span style={{ fontSize: 13, fontWeight: 400, color: inkMuted, marginTop: 4, ...ellipsis }}>{sub}</span>
      ) : null}
      {covers.length ? (
        <div data-testid="deck-fan" style={{ position: 'relative', height: DECK_COVER.h, width: fanWidth, marginTop: 14 }}>
          {covers.map((c, i) => (
            <div
              key={c.key ?? c.src ?? `${c.color ?? ''}-${i}`}
              data-testid="deck-cover"
              style={{
                position: 'absolute', left: i * DECK_COVER.step, top: 0, zIndex: i,
                width: DECK_COVER.w, height: DECK_COVER.h, borderRadius: 10, overflow: 'hidden', boxSizing: 'border-box',
                background: c.color || 'var(--surface-3)', border: '1px solid var(--line-soft)', boxShadow: 'var(--shadow-2)',
              }}
            >
              {c.src ? <Image props={{ src: c.src, fit: 'cover', ratio: '61 / 81' }} /> : null}
            </div>
          ))}
        </div>
      ) : null}
    </button>
  );
}

/**
 * ThoughtCard (L1) — one unprompted remark from the agent, drawn INVERTED so
 * it reads as a voice and not as a module.
 *
 * A round glyph disc on the leading edge (the speaker's mark) · an uppercase
 * eyebrow naming the kind of remark · the text, WRAPPED at a reading
 * line-height (a thought is read, never clipped) · a hint line saying what
 * tapping does. It carries its own surface — ink on text-colour, the palette
 * turned inside out — so on a dashboard the node is `bare: true`. The whole
 * card is the tap target and emits `activated`; the hint is a promise about
 * the card, not a button on it. Spacing is the design's own (16 padding, 16
 * radius, 32 disc, 12 gap, 4 · 8 between the lines).
 */
export function ThoughtCard({ props = {}, data, emit, slots }) {
  const pushed = slots?.setThought?.payload || {};
  const pick = (name) => (pushed[name] !== undefined ? pushed[name] : (props[name] !== undefined ? props[name] : data?.[name]));
  const glyph = props.glyph ?? '✦';
  const eyebrow = props.eyebrow ?? (data ? data.eyebrow : null);
  const text = pick('text');
  const hint = pick('hint');

  return (
    <button
      type="button"
      data-testid="thought-card"
      onClick={emit ? () => emit('activated', {}) : undefined}
      style={{
        ...resetButton, display: 'flex', alignItems: 'flex-start', gap: 12,
        width: '100%', minWidth: 0, padding: 16, borderRadius: 16, boxSizing: 'border-box',
        background: 'var(--text)', color: 'var(--surface)',
        cursor: emit ? 'pointer' : 'default',
      }}
    >
      <span
        data-testid="thought-glyph"
        aria-hidden
        style={{
          flex: '0 0 auto', width: 32, height: 32, borderRadius: 16, background: 'var(--text-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, lineHeight: 1,
        }}
      >{glyph}</span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        {eyebrow ? (
          <span style={{
            fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4,
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{eyebrow}</span>
        ) : null}
        {text ? (
          <span data-testid="thought-text" style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.5, whiteSpace: 'normal' }}>{text}</span>
        ) : null}
        {hint ? (
          <span data-testid="thought-hint" style={{ fontSize: 12, fontWeight: 600, marginTop: 8 }}>{hint}</span>
        ) : null}
      </span>
    </button>
  );
}
export const ProposalCard = ownCard(ProposalCardBare, 'ProposalCard');
export const NotePanel = ownCard(NotePanelBare, 'NotePanel');
export const DeliveryCard = ownCard(DeliveryCardBare, 'DeliveryCard');
